<template>
  <!-- 客户跟进记录：与商机跟进共用一张表，这里是按对象类型筛出的客户视图 -->
  <div class="customer-record-list">
    <list-page>
      <search-condition @search="resetSearch" :show-search="true" page-name="customerRecordList">
        <template #default>
          <customer-select v-model="searchInfo.partnerId" placeholder="全部客户" @change="resetSearch">
            <template #prefix>客户</template>
          </customer-select>
          <el-input v-model="searchInfo.searchKey" placeholder="跟进内容" @keydown.enter="resetSearch" clearable>
            <template #prefix>关键字</template>
          </el-input>
          <employee-select v-model="searchInfo.followEmployeeId" placeholder="全部" @change="resetSearch">
            <template #prefix>跟进人</template>
          </employee-select>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
          <page-button type="write" el-type="success" @click="openDialog">+ 新建跟进</page-button>
        </template>
      </search-condition>

      <list-table v-loading="loading" background-color="#FFF">
        <yq-table :data="dataList" :empty-text="'暂无数据'" height="100%" page-name="customerRecordList">
          <yq-table-column label="客户名称" prop="partnerTitle" min-width="180rem" :show-overflow-tooltip="true"/>
          <yq-table-column label="跟进内容" prop="followContent" min-width="260rem" :show-overflow-tooltip="true"/>
          <yq-table-column label="跟进人" prop="followEmployeeName" width="120rem"/>
          <yq-table-column label="跟进时间" prop="followTime" width="180rem"/>
          <!-- 只显示到天：下次跟进是「哪天再联系」的安排，带上时分秒没人看 -->
          <yq-table-column label="下次跟进" prop="nextFollowDate" width="120rem">
            <template v-slot="{row}">{{ dateOnly(row.nextFollowDate) }}</template>
          </yq-table-column>
        </yq-table>
      </list-table>

      <form-page v-model:page-number="searchInfo.pageNumber" v-model:page-size="searchInfo.pageSize"
                 :total-count="searchInfo.totalCount" @onLoad="queryList"/>
    </list-page>

    <!-- 新建跟进：客户必选，否则记录挂不到任何人身上 -->
    <el-dialog v-model="showDialog" title="新建跟进" dialog-width="2" :close-on-click-modal="false">
      <form-items :data="dialogData" :columns="2" label-width="2.4rem" icon-width="0" ref="dialogForm">
        <form-item label="客户" :required="true" name="partnerId" error-message="请选择客户" :show-block="true">
          <customer-select v-model="dialogData.partnerId" placeholder="请选择客户" style="width: 100%"/>
        </form-item>
        <form-item label="跟进内容" :required="true" name="followContent" error-message="请填写跟进内容" :show-block="true">
          <el-input v-model="dialogData.followContent" type="textarea" :rows="3" placeholder="本次沟通的情况"/>
        </form-item>
        <!-- 默认当天，可手动改。只取到日，避免打开弹窗的时刻让「今天」漂到昨天 -->
        <form-item label="跟进时间">
          <el-date-picker v-model="dialogData.followTime" type="date" value-format="YYYY-MM-DD" style="width: 100%"/>
        </form-item>
        <!-- 填了会回写到客户信息表，不填则清空客户信息表上的下次跟进时间 -->
        <form-item label="下次跟进">
          <el-date-picker v-model="dialogData.nextFollowDate" type="date" value-format="YYYY-MM-DD" style="width: 100%"/>
        </form-item>
      </form-items>
      <template #footer>
        <page-button @click="showDialog = false">取消</page-button>
        <page-button type="write" el-type="primary" @click="saveFollow">保存</page-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import FormPage from "@/components/h5/layout/FormPage.vue";
import FormItems from "@/components/h5/layout/FormItems.vue";
import FormItem from "@/components/h5/layout/FormItem.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import EmployeeSelect from "@/components/h5/data/EmployeeSelect.vue";
import CustomerSelect from "@/components/h5/partner/CustomerSelect.vue";
import YqTable from "@/components/h5/table/YqTable.vue";
import customerFollowApi from "@/api/normal/common/customerFollowApi";
import Util from "@/util/util";
import {useRoute, useRouter} from "vue-router";

/** 取本地当天的 yyyy-MM-dd。不能用 toISOString()：它是 UTC，东八区凌晨会退到前一天 */
function today() {
  const d = new Date();
  const pad = n => String(n).padStart(2, "0");
  return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
}

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "CustomerRecordList",
  components: {
    YqTable, EmployeeSelect, CustomerSelect, PageButton, SearchCondition, FormPage,
    FormItems, FormItem, ListTable, ListPage
  },
  data() {
    return {
      loading: false,
      searchInfo: {pageNumber: 1, pageSize: 20, partnerId: "", searchKey: "", followEmployeeId: ""},
      dataList: [],
      showDialog: false,
      dialogData: {},
    };
  },
  beforeRouteLeave(to, from, next) {
    // 将当前页面数据进行缓存
    Util.cachePageData(this, to, from);
    next();
  },
  created() {
    if (!Util.loadPageData(this)) { // 加载缓存数据失败，初始化页面
      this.resetSearch();
    }
  },
  methods: {
    /** 日期只取到天（后端返回可能带时间部分） */
    dateOnly(value) {
      return value ? String(value).slice(0, 10) : "";
    },
    async queryList() {
      // 空值不提交，避免空串绑到后端 Integer 报错
      const params = {};
      Object.keys(this.searchInfo).forEach(k => {
        const v = this.searchInfo[k];
        if (v === "" || v === null || v === undefined) {
          return;
        }
        params[k] = v;
      });
      this.loading = true;
      try {
        const {records, totalCount} = await customerFollowApi.queryList(params);
        this.dataList = records || [];
        this.searchInfo.totalCount = totalCount;
      } finally {
        this.loading = false;
      }
    },
    resetSearch() {
      this.searchInfo.pageNumber = 1;
      this.queryList();
    },
    openDialog() {
      this.dialogData = {
        // 已经按客户筛过时直接带入，省一次选择
        partnerId: this.searchInfo.partnerId || "",
        followContent: "",
        followTime: today(),
        nextFollowDate: "",
      };
      this.showDialog = true;
    },
    async saveFollow() {
      if (!this.$refs.dialogForm.validCheck()) {
        return;
      }
      await customerFollowApi.saveFollow(this.dialogData);
      this.showDialog = false;
      this.resetSearch();
    },
  }
};
</script>

<style lang="scss" scoped>
</style>
